import React from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import Helmet, { HelmetProps } from 'react-helmet'
import { mapToSingleObject } from '@/utils/helpers'
import { SanityMeta } from 'web/types/graphql-types'
import '@/assets/fonts.css'

type MetaData = {
  robotsIndex: boolean
  metaTitle: string | null
  metaDescription: string | null
  ogImage: unknown
}

type HeadProps = {
  title?: string
  metaData?: SanityMeta
} & HelmetProps

const Head = (props: HeadProps): JSX.Element => {
  const { metaData, title } = props

  const { data } = useStaticQuery(graphql`
    query {
      data: allSanitySiteSettingsMeta {
        edges {
          node {
            meta {
              robotsIndex
              metaTitle
              metaDescription
              ogImage {
                asset {
                  url
                }
              }
            }
          }
        }
      }
    }
  `)

  const { meta: defaultMeta } = mapToSingleObject(data)

  // if this is not the production environment, we don't want to index the site
  // noindex set in general siteSettings has priority over page-specific setting
  let indexSafe = 'noindex,nofollow'

  if (process.env.STAGE === 'production') {
    indexSafe =
      defaultMeta?.robotsIndex && metaData?.robotsIndex
        ? 'index,follow'
        : 'noindex,follow'
  }

  const ogImage = metaData?.ogImage || defaultMeta?.ogImage

  return (
    <Helmet
      titleTemplate={`%s — ${defaultMeta?.metaTitle}`}
      defaultTitle={defaultMeta?.metaTitle}
      title={title}
      {...props}
    >
      <html lang="en" />
      <meta name="robots" content={indexSafe} />
      <meta
        name="description"
        content={metaData?.metaDescription || defaultMeta?.metaDescription}
      />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1, shrink-to-fit=no"
      />
      {/* <style type="text/css">{fonts}</style> */}
      {ogImage && <meta property="og:image" content={ogImage.asset.url} />}
    </Helmet>
  )
}

export const query = graphql`
  fragment metaData on SanityMeta {
    robotsIndex
    metaTitle
    metaDescription
    ogImage {
      asset {
        url
      }
    }
  }
`

export default Head
